// src/components/DomainSetupModal.jsx
import { useState, useEffect } from 'react';
import { X, Globe, Copy, CheckCircle, ExternalLink, ChevronRight, Loader, AlertCircle, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

function CopyBox({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] font-mono text-sm">
      <span className="flex-1 text-[var(--text-primary)] truncate">{value}</span>
      <button
        onClick={copy}
        className="flex-shrink-0 p-1 rounded hover:bg-[var(--bg-tertiary)] transition-all"
        title="Copy"
      >
        {copied
          ? <CheckCircle className="w-4 h-4 text-[var(--accent-success)]" />
          : <Copy className="w-4 h-4 text-[var(--text-muted)]" />
        }
      </button>
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/30 flex items-center justify-center">
        <span className="text-xs font-bold text-[var(--accent-primary)]">{number}</span>
      </div>
      <div className="flex-1 pt-0.5">
        <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{title}</p>
        {children}
      </div>
    </div>
  );
}

const POPULAR_PROVIDERS = [
  { name: 'Namecheap', url: 'https://www.namecheap.com/support/knowledgebase/article.aspx/319/2237/how-can-i-set-up-an-a-address-record-for-my-domain/' },
  { name: 'GoDaddy', url: 'https://uk.godaddy.com/help/add-an-a-record-19238' },
  { name: 'Cloudflare', url: 'https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/' },
  { name: 'Google Domains', url: 'https://support.google.com/domains/answer/3290350' },
];

export default function DomainSetupModal({ onClose, currentDomain }) {
  const [ip, setIp] = useState(null);
  const [loadingIp, setLoadingIp] = useState(true);
  const [ipError, setIpError] = useState(false);

  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch('/api/broker/domain/setup-info', {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
        });
        const data = await res.json();
        if (data.success && data.data.server_ip) {
          setIp(data.data.server_ip);
        } else {
          setIpError(true);
        }
      } catch {
        setIpError(true);
      } finally {
        setLoadingIp(false);
      }
    };
    fetchIp();
  }, []);

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onBackdrop}
    >
      <div className="w-full max-w-2xl bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--accent-primary)]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[var(--accent-primary)]" />
            </div>
            <div>
              <h3 className="font-bold text-[var(--text-primary)]">How to connect your domain</h3>
              <p className="text-xs text-[var(--text-muted)]">Point your domain to this platform</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all"
          >
            <X className="w-4 h-4 text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

          {/* Current domain URLs */}
          {currentDomain && (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/20">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[var(--accent-primary)]" />
                  <span className="text-xs font-semibold text-[var(--text-muted)]">Trader Dashboard</span>
                </div>
                <span className="font-mono text-sm font-semibold text-[var(--accent-primary)]">
                  https://{currentDomain}
                </span>
              </div>
              <div className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-[var(--accent-warning)]/5 border border-[var(--accent-warning)]/20">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[var(--accent-warning)]" />
                  <span className="text-xs font-semibold text-[var(--text-muted)]">Admin Dashboard</span>
                </div>
                <span className="font-mono text-sm font-semibold text-[var(--accent-warning)]">
                  https://admin.{currentDomain}
                </span>
              </div>
            </div>
          )}
          
          {/* Step 1 */}
          <Step number="1" title="Log in to your domain provider">
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              Open the DNS management panel for your domain. Common providers:
            </p>

            <div className="grid grid-cols-2 gap-2">
              {POPULAR_PROVIDERS.map((p) => (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] border border-[var(--border-color)] text-sm transition-all group"
                >
                  <span className="font-medium text-[var(--text-primary)]">
                    {p.name}
                  </span>
                  <ExternalLink className="w-3 h-3 text-[var(--text-muted)] group-hover:text-[var(--accent-primary)] transition-colors" />
                </a>
              ))}
            </div>
          </Step>

          <div className="border-t border-[var(--border-color)]" />

          {/* Step 2 */}
          <Step number="2" title="Create TWO A Records (Trader + Admin dashboards)">
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              Add both records in your DNS settings:
            </p>

            <div className="rounded-xl border border-[var(--border-color)] overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-4 px-3 py-2 bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Record</span>
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Type</span>
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Host</span>
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Value</span>
              </div>
              
              {/* Row 1: Main domain (trader) */}
              <div className="grid grid-cols-4 px-3 py-3 gap-2 border-b border-[var(--border-color)]">
                <div className="flex items-center">
                  <span className="px-2 py-1 rounded-md bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] text-xs font-bold">
                    Trader
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-500 text-xs font-mono">A</span>
                </div>
                <div>
                  <CopyBox value="@" label="Host" />
                </div>
                <div>
                  {loadingIp ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
                      <Loader className="w-4 h-4 animate-spin text-[var(--text-muted)]" />
                    </div>
                  ) : ipError ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--accent-danger)]/5 border border-[var(--accent-danger)]/20">
                      <AlertCircle className="w-4 h-4 text-[var(--accent-danger)]" />
                    </div>
                  ) : (
                    <CopyBox value={ip} label="IP" />
                  )}
                </div>
              </div>

              {/* Row 2: Admin subdomain */}
              <div className="grid grid-cols-4 px-3 py-3 gap-2">
                <div className="flex items-center">
                  <span className="px-2 py-1 rounded-md bg-[var(--accent-warning)]/10 text-[var(--accent-warning)] text-xs font-bold">
                    Admin
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-500 text-xs font-mono">A</span>
                </div>
                <div>
                  <CopyBox value="admin" label="Host" />
                </div>
                <div>
                  {loadingIp ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
                      <Loader className="w-4 h-4 animate-spin text-[var(--text-muted)]" />
                    </div>
                  ) : ipError ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--accent-danger)]/5 border border-[var(--accent-danger)]/20">
                      <AlertCircle className="w-4 h-4 text-[var(--accent-danger)]" />
                    </div>
                  ) : (
                    <CopyBox value={ip} label="IP" />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <p className="text-xs text-[var(--text-secondary)]">
                <strong className="text-[var(--text-primary)]">Important:</strong> Both records point to the same IP address. 
                The <code className="px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--accent-primary)]">@</code> record is for traders, 
                and <code className="px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--accent-warning)]">admin</code> is for your broker admin panel.
              </p>
            </div>
          </Step>

          <div className="border-t border-[var(--border-color)]" />

          {/* Step 3 */}
          <Step number="3" title="Save and wait for DNS propagation">
            <p className="text-sm text-[var(--text-secondary)]">
              After saving, DNS changes typically take <strong>a few minutes</strong> to a few hours to propagate worldwide. Once done, both URLs will be live.
            </p>
            <div className="mt-3 flex items-start gap-2 px-3 py-3 rounded-lg bg-[var(--accent-success)]/5 border border-[var(--accent-success)]/20">
              <CheckCircle className="w-4 h-4 text-[var(--accent-success)] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--text-secondary)]">
                Then come back here, enter your domain in the <strong>Domain</strong> field, and save your settings.
              </p>
            </div>
          </Step>

          <div className="border-t border-[var(--border-color)]" />

          {/* Step 4 */}
          <Step number="4" title="SSL is handled automatically">
            <p className="text-sm text-[var(--text-secondary)]">
              Once both DNS records resolve, SSL/HTTPS is provisioned automatically for both the trader and admin dashboards. No extra action needed.
            </p>
          </Step>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white text-sm font-semibold transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}